import React, { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import FormHelperText from "@mui/material/FormHelperText";
import CloseIcon from "@mui/icons-material/Close";
import AssetQuantityField from "./AssetQuantityField";

export default function QuantityEditModal({
  onClose,
  value,
  onChange,
  availableQuantity,
  minQuantity = 0,
  quantityLabel = "Quantity Requested",
}) {
  const [tempQuantity, setTempQuantity] = useState(value);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const maxQuantity = useMemo(() => {
    return Number.isFinite(availableQuantity) ? value + availableQuantity : null;
  }, [availableQuantity, value]);

  const validateQuantity = quantity => {
    if (!Number.isInteger(quantity)) {
      return "Enter a whole number";
    }

    if (quantity < minQuantity) {
      return `Quantity must be at least ${minQuantity}`;
    }

    if (maxQuantity !== null && quantity > maxQuantity) {
      return `Only ${maxQuantity} total can be requested right now`;
    }

    return "";
  };

  const handleTempQuantityChange = quantity => {
    setTempQuantity(quantity);
    if (error) setError("");
  };

  const handleSave = async () => {
    const nextError = validateQuantity(tempQuantity);
    if (nextError) {
      setError(nextError);
      return;
    }

    setSaving(true);
    const result = await onChange(tempQuantity);
    setSaving(false);

    if (result === false) return;

    const submitError =
      typeof result === "string" ? result : result?.quantity || result?.submit || result?.asset;

    if (submitError) {
      setError(submitError);
      return;
    }

    onClose();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Typography fontSize={22} fontWeight={500} color="text.secondary" align="center">
          Change Quantity Requested
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
          {Number.isFinite(availableQuantity) && (
            <Typography color="error" sx={{ mb: 2 }}>
              Note: {availableQuantity} more available
            </Typography>
          )}
          <AssetQuantityField
            quantity={tempQuantity}
            onChange={handleTempQuantityChange}
            size="large"
            disablePlus={maxQuantity !== null && tempQuantity >= maxQuantity}
            disableMinus={tempQuantity <= minQuantity}
            quantityProps={{
              label: quantityLabel,
              style: { minWidth: 160 },
              InputLabelProps: {
                shrink: true,
              },
              autoFocus: true,
              onKeyDown: event => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSave();
                }
              },
            }}
          />
          {error && (
            <FormHelperText error sx={{ mt: 1 }}>
              {error}
            </FormHelperText>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
